import { urls } from "../constants";
import api from "./api"; // Updated import

const getSpecialties = async () => {
  const response = await api.get(urls.getSpecialties);
  return {
    status: response.status,
    response: response.data,
  };
};

const addUserDoctor = async ({ data }) => {
  data.doctor.phone = "+56" + data.doctor.phone;

  const response = await api.post(urls.addUserDoctor, {
    ...data,
  });
  return {
    status: response.status,
    response: response.data,
  };
};

const editDoctorProfile = async ({ user, _id }) => {
  const response = await api.put(`${urls.editDoctorProfile}/${_id}`, {
    ...user,
  });
  return {
    status: response.status,
    response: response.data,
  };
};

const loginUserDoctor = async ({ rut, password }) => {
  // Login still needs to handle errors manually if we want to extract specific fields
  // BUT api.js handles 401. However, login normally returns 200 or 400.
  // We can let api.js handle it, but standard login usually doesn't need the token header (it doesn't have one yet)
  // and we might need to handle 400 validation errors specifically.
  try {
      const response = await api.post(urls.loginDoctor, {
        username: rut,
        password,
      });
      return {
        status: response.status,
        response: response.data,
      };
  } catch(error) {
      if (error.response) {
          return {
              status: error.response.status,
              response: error.response.data
          }
      }
      throw error;
  }
};

const getDoctorKit = async ({ _id }) => {
  const response = await api.get(`${urls.getDoctorKit}?doctorId=${_id}`);
  return {
    status: response.status,
    response: response.data,
  };
};

const getOrdersList = async ({ _id }) => {
  const response = await api.get(`${urls.getOrdersList}?doctorId=${_id}`);
  return {
    status: response.status,
    response: response.data,
  };
};

const getPatiensAssignProducts = async ({ _id }) => {
  const response = await api.get(`${urls.getPatiensAssignProducts}?doctorId=${_id}`);
  return {
    status: response.status,
    response: response.data,
  };
};

const addDoctorReport = async ({ doctorId, laboratoryId, report }) => {
  const response = await api.post(urls.addDoctorReport, {
    doctorId,
    laboratoryId,
    report,
  });
  return {
    status: response.status,
    response: response.data,
  };
};

const sendTokenResetPassword = async ({ rut }) => {
  try {
    const response = await api.post(urls.sendTokenResetPassword, {
        rut,
    });
    return {
        status: response.status,
        response: response.data,
    };
  } catch(error) {
      if(error.response) {
           return {
             status: error.response.status,
             response: error.response.data
           }
      }
      throw error;
  }
};

const updatePassword = async ({ token, password }) => {
  try {
    const response = await api.post(urls.updatePassword, {
        token,
        password,
    });
    return {
        status: response.status,
        response: response.data,
    };
  } catch(error) {
      if(error.response) {
          return {
             status: error.response.status,
             response: error.response.data
           }
      }
      throw error;
  }
};

const getNotifications = async ({ doctorId, page, limit }) => {
  const response = await api.get(`${urls.notifications}?doctorId=${doctorId}&page=${page}&limit=${limit}`);
  return {
    status: response.status,
    response: response.data,
  };
};

const updateNotificationsStatus = async ({ notifications }) => {
  const response = await api.put(urls.notifications, {
    notifications,
  });
  return {
    status: response.status,
    response: response.data,
  };
};

const verifyTokenValidity = async () => {
  const response = await api.get(urls.verifyTokenValidity);
  
  // Need to extract the response data correctly as per original code
  // Original:
  // const response = await request.json();
  // const formattedUser = { ...response.user... };
  // return { status, response: formattedUser };

  const resData = response.data;
  // Note: verifyTokenValidity response structure might need to be preserved or adapted.
  // The original code accessed response.user manually.
  
  // However, wait. The original code used `token` as argument. 
  // Now `api.js` attaches the token from localStorage automatically.
  // But wait, sometimes verifyToken might be called with a specific token (e.g. from URL or redirect)?
  // Checking usage: AuthHandler calls it with { token }
  // If it's called with a token, we might want to respect that override.
  // But `api.js` uses strict interceptor.
  
  // Let's modify api.js request interceptor to allow overriding? Or just set the header here if needed.
  // Actually, AuthContext/Handler seems to use it to verify a token that might just have been received?
  // Let's look at AuthHandler.js again later.
  
  // For now assuming existing token in localStorage or we rely on the interceptor.
  // BUT AuthHandler passes { token } explicitly.
  // If we want to support passing explicit token, we can pass config to api.get
  
  // Let's adjust verifyTokenValidity signature to accept token (optional)
  
  const formattedUser = {
    _id: resData.user._id,
    role: resData.user.role,
    email: resData.user.email,
    token: resData.token || localStorage.getItem("token"), // Or from response?
    username: resData.user.username,
    doctor: resData.user.doctor[0],
  };
  return {
    status: response.status,
    response: formattedUser,
  };
};

// Updating verifyTokenValidity to accept token
const verifyTokenValidityWithArg = async ({ token } = {}) => {
    const config = {};
    if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
    }
    const response = await api.get(urls.verifyTokenValidity, config);
    
    // We recreate the formatting logic
    const resData = response.data;
    const formattedUser = {
        _id: resData.user._id,
        role: resData.user.role,
        email: resData.user.email,
        token: token || localStorage.getItem("token"), 
        username: resData.user.username,
        doctor: resData.user.doctor[0],
    };

    return {
        status: response.status,
        response: formattedUser
    }
}


const getDoctorSignedConsents = async ({ _id }) => {
  const response = await api.get(`${urls.getDoctorSignedConsents}/${_id}`);
  return {
    status: response.status,
    response: response.data,
  };
};

const verifyEmail = async ({ token }) => {
    try {
        const response = await api.post(urls.verifyEmail, { token });
        return { status: response.status, response: response.data };
    } catch (error) {
        return {
            status: error.response?.status || 0,
            response: error.response?.data || { error: 'Error de conexión' },
        };
    }
};

// Logout function
const logout = async ({ refreshToken }) => {
    const response = await api.post(urls.logout, {
        refreshToken
    });
    return {
        status: response.status,
        response: response.data
    };
}


const resendVerificationEmail = async ({ username }) => {
  try {
    const response = await api.post(urls.resendVerificationEmail, { username });
    return { status: response.status, response: response.data };
  } catch (error) {
    if (error.response) {
      return { status: error.response.status, response: error.response.data };
    }
    throw error;
  }
};

const doctorServices = Object.freeze({
  getSpecialties,
  addUserDoctor,
  loginUserDoctor,
  getDoctorKit,
  getPatiensAssignProducts,
  getOrdersList,
  editDoctorProfile,
  addDoctorReport,
  sendTokenResetPassword,
  updatePassword,
  getNotifications,
  updateNotificationsStatus,
  verifyTokenValidity: verifyTokenValidityWithArg, // Use the one with arg support
  getDoctorSignedConsents,
  verifyEmail,
  resendVerificationEmail,
  logout, // Add logout
});

export default doctorServices;
